import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';
import { IconButton, Box, Typography, Tooltip } from '@mui/material';
import Badge from '@mui/material/Badge';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import dayjs from 'dayjs';
import { useState, useMemo } from 'react';

const createShouldDisableDate = (highlightedSet) => {
  const formattedDateCache = new Map();
  return (day) => {
    // day is a dayjs object (per MUI X DatePicker API)
    const dayValue = day.valueOf();
    let formatted = formattedDateCache.get(dayValue);
    if (!formatted) {
      formatted = day.format('YYYY-MM-DD');
      formattedDateCache.set(dayValue, formatted);
    }
    return !highlightedSet.has(formatted);
  };
};

function ButtonField(props) {
  const {
    setOpen,
    label,
    id,
    disabled,
    InputProps: { ref } = {},
    inputProps: { 'aria-label': ariaLabel } = {},
  } = props;

  return (
    <Box display='flex' alignItems='center'>
      <IconButton
        id={id}
        disabled={disabled}
        ref={ref}
        aria-label={ariaLabel}
        onClick={() => setOpen?.((prev) => !prev)}
      >
        <CalendarMonthOutlinedIcon />
      </IconButton>
      <Typography variant='body1' marginLeft={1}>
        {label}
      </Typography>
    </Box>
  );
}

// Custom day renderer to highlight specific dates
function HighlightedDay(props) {
  const {
    day,
    outsideCurrentMonth,
    disabled,
    highlightedDays = new Set(),
    ...other
  } = props;
  const isHighlighted = highlightedDays.has(day.format('YYYY-MM-DD'));

  const showNoDataTooltip =
    Boolean(disabled) && !outsideCurrentMonth && !isHighlighted;

  const badgeDay = (
    <Badge
      overlap='circular'
      variant='dot'
      color='primary'
      invisible={!isHighlighted}
    >
      <PickersDay
        {...other}
        day={day}
        outsideCurrentMonth={outsideCurrentMonth}
        disabled={disabled}
      />
    </Badge>
  );

  if (showNoDataTooltip) {
    return (
      <Tooltip
        title='No voyage or shipment history data on this date'
        placement='top'
        arrow
        enterDelay={1000}
      >
        <span>{badgeDay}</span>
      </Tooltip>
    );
  }

  return badgeDay;
}

function ButtonDatePicker(props) {
  const [open, setOpen] = useState(false);

  const highlightedSet = useMemo(() => {
    const dates = Array.isArray(props.highlightedDates)
      ? props.highlightedDates
      : [];
    return new Set(
      dates
        .filter(Boolean)
        .map((d) => dayjs(d))
        .filter((d) => d.isValid())
        .map((d) => d.format('YYYY-MM-DD'))
    );
  }, [props.highlightedDates]);

  const shouldDisableDate = useMemo(
    () => createShouldDisableDate(highlightedSet),
    [highlightedSet]
  );

  return (
    <DatePicker
      {...props}
      slots={{ ...props.slots, field: ButtonField, day: HighlightedDay }}
      slotProps={{
        ...props.slotProps,
        field: { setOpen },
        day: { highlightedDays: highlightedSet },
      }}
      shouldDisableDate={shouldDisableDate}
      open={open}
      onClose={() => setOpen(false)}
      onOpen={() => setOpen(true)}
    />
  );
}

export default function IconButtonDatePicker({
  value,
  onChange,
  highlightedDates = [],
}) {
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <ButtonDatePicker
        label={value ? value.format('DD-MMM-YYYY') : 'Select Date'}
        value={value}
        onChange={onChange}
        highlightedDates={highlightedDates}
      />
    </LocalizationProvider>
  );
}
